import moment from 'moment-timezone';
import { TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import type { LongDateFormatKey } from 'moment-timezone';

const localizedTokens = [
  'LTS',
  'LT',
  'L',
  'LL',
  'LLL',
  'LLLL',
  'lts',
  'lt',
  'l',
  'll',
  'lll',
  'llll',
] as readonly LongDateFormatKey[];

const localizedTokenSet: ReadonlySet<LongDateFormatKey> = new Set(localizedTokens);

const isLocalizedToken = (token: string): token is LongDateFormatKey => {
  return localizedTokenSet.has(token as LongDateFormatKey);
};

export const expandLocalizedTokens = (formatString: string, locale: string = moment.locale()) => {
  const localeData = moment.localeData(locale);

  return formatString
    .split(/(\s+)/)
    .map((token) => (isLocalizedToken(token) ? localeData.longDateFormat(token) : token))
    .join('');
};

const timeTokens = /\b(HH?|hh?|mm?|ss?|a|A)\b/;
export const formatHasTimeElements = (dateFormat: string) => timeTokens.test(dateFormat);

const twentyFourHourTimeTokens = /\b(HH?)\b/;
export const formatUses24HourTime = (dateFormat: string) => twentyFourHourTimeTokens.test(dateFormat);

/**
 * Converts a moment.js format string from 12-hour to 24-hour time by replacing
 * lowercase hour tokens (h/hh) with their 24-hour equivalents (H/HH) and
 * removing the AM/PM indicator (A/a).
 *
 * @param formatString - A moment.js format string, e.g. 'MMM D, YYYY h:mm A'
 * @returns The format string using 24-hour time, e.g. 'MMM D, YYYY H:mm'
 */
export const convertTo24HourFormat = (formatString: string): string =>
  formatString
    .replace(/\bhh\b/g, 'HH')
    .replace(/\bh\b/g, 'H')
    .replace(/\s*\b[aA]\b/g, '');

/**
 * Returns the preferred moment.js time format string based on the user's 24-hour
 * format preference. Defaults to TIME_FORMAT_NUMERICAL_SHORT when no base format
 * is provided.
 *
 * @param use24Hour - Whether to use 24-hour time format
 * @param baseFormat - The base moment.js format string to convert (defaults to TIME_FORMAT_NUMERICAL_SHORT)
 * @returns The format string adjusted for the user's preference
 */
export const getPreferredTimeFormat = (use24Hour: boolean, baseFormat = TIME_FORMAT_NUMERICAL_SHORT): string =>
  use24Hour ? convertTo24HourFormat(expandLocalizedTokens(baseFormat)) : baseFormat;
